import * as React from 'react';
import * as AdaptiveCards from 'adaptivecards';
import MarkdownIt from 'markdown-it';
import { makeStyles, tokens } from '@fluentui/react-components';

// Import centralized interfaces
import type { AdaptiveCardMessageProps } from '../interfaces';

const useStyles = makeStyles({
  adaptiveCardContainer: {
    fontSize: '14px',
    lineHeight: 1.4,
    color: tokens.colorNeutralForeground1, // M365 text color
    fontFamily: tokens.fontFamilyBase, // Segoe UI
    width: '100%',
    
    // Adaptive Cards SDK container styling
    '& .ac-container': {
      fontFamily: 'inherit',
    },
    
    // Submit button - M365 Primary Button style
    '& .ac-pushButton': {
      cursor: 'pointer',
      backgroundColor: `${tokens.colorBrandBackground} !important`, // M365 brand blue
      color: `${tokens.colorNeutralForegroundOnBrand} !important`, // White text
      border: 'none !important',
      borderRadius: `${tokens.borderRadiusMedium} !important`, // 4px
      padding: '10px 20px !important',
      fontSize: tokens.fontSizeBase300, // 14px
      fontWeight: tokens.fontWeightSemibold, // 600
      width: '100% !important',
      textAlign: 'center',
      transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1) !important', // M365 easing
      boxShadow: `${tokens.shadow4} !important`,
      
      '&:hover': {
        backgroundColor: `${tokens.colorBrandBackgroundHover} !important`,
        boxShadow: `${tokens.shadow8} !important`,
      },
      
      '&:active': {
        backgroundColor: `${tokens.colorBrandBackgroundPressed} !important`,
        boxShadow: `${tokens.shadow2} !important`,
      },
      
      '&:focus': {
        outline: `2px solid ${tokens.colorStrokeFocus2} !important`, // M365 focus ring
        outlineOffset: '2px !important',
      }
    },
    
    // Input fields - M365 Input style
    '& .ac-input': {
      border: `1px solid ${tokens.colorNeutralStroke1} !important`,
      borderRadius: `${tokens.borderRadiusMedium} !important`,
      padding: '8px 12px !important',
      fontSize: tokens.fontSizeBase300,
      fontFamily: 'inherit !important',
      width: '100% !important',
      boxSizing: 'border-box',
      backgroundColor: `${tokens.colorNeutralBackground1} !important`,
      color: `${tokens.colorNeutralForeground1} !important`,
      
      '&:focus': {
        outline: 'none !important',
        border: `2px solid ${tokens.colorBrandStroke1} !important`,
        padding: '7px 11px !important',
      },
      
      '&::placeholder': {
        color: `${tokens.colorNeutralForeground4} !important`,
      }
    },
    
    // Multiline textarea
    '& .ac-input.ac-textInput.ac-multiline': {
      minHeight: '80px !important',
      resize: 'vertical',
    },
    
    // Label styling - M365 Label
    '& .ac-input-label': {
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      color: `${tokens.colorNeutralForeground1} !important`,
      marginBottom: '4px !important',
      display: 'block !important',
    },
    
    // Required field indicator
    '& .ac-input-label .ac-input-required': {
      color: `${tokens.colorPaletteRedForeground1} !important`, // M365 error red
    },
    
    // Action container
    '& .ac-actionSet': {
      marginTop: '12px !important',
    }
  },
  errorContainer: {
    padding: '12px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorPaletteRedForeground1,
    fontSize: '14px',
  },
  fallbackContainer: {
    padding: '12px',
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '14px',
  }
});

/**
 * AdaptiveCardMessage Component
 * 
 * Renders Adaptive Card JSON payloads using the official Adaptive Cards SDK.
 * Supports:
 * - All standard Adaptive Card elements (TextBlock, Image, FactSet, ColumnSet, etc.)
 * - Input controls (Input.Text, Input.ChoiceSet, etc.)
 * - Actions (Action.Submit, Action.OpenUrl)
 * - Safe rendering with error boundaries
 * 
 * @param cardJson - The Adaptive Card JSON object
 * @param onAction - Callback for Action.Submit events
 * @param fallbackText - Optional fallback text if card rendering fails
 */
const AdaptiveCardMessage: React.FC<AdaptiveCardMessageProps> = ({
  cardJson,
  onAction,
  fallbackText
}) => {
  const styles = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [renderError, setRenderError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;

    let adaptiveCard: AdaptiveCards.AdaptiveCard | null = null;

    try {
      // Clear any previous content
      containerRef.current.innerHTML = '';
      setRenderError(null);

      // Enable markdown processing for Adaptive Cards
      const md = new MarkdownIt();
      AdaptiveCards.AdaptiveCard.onProcessMarkdown = (text, result) => {
        result.outputHtml = md.render(text);
        result.didProcess = true;
      };

      // Create and configure Adaptive Card instance
      adaptiveCard = new AdaptiveCards.AdaptiveCard();
      
      // Configure host config for better styling integration
      adaptiveCard.hostConfig = new AdaptiveCards.HostConfig({
        fontFamily: 'Segoe UI, "Segoe UI Web (West European)", Arial, sans-serif',
        spacing: {
          small: 4,
          default: 8,
          medium: 12,
          large: 16,
          extraLarge: 24,
          padding: 12
        },
        separator: {
          lineThickness: 1,
          lineColor: '#E1DFDD'
        },
        fontSizes: {
          small: 12,
          default: 14,
          medium: 16,
          large: 20,
          extraLarge: 24
        },
        fontWeights: {
          lighter: 200,
          default: 400,
          bolder: 600
        },
        containerStyles: {
          default: {
            backgroundColor: '#FFFFFF',
            foregroundColors: {
              default: {
                default: '#323130',
                subtle: '#605E5C'
              },
              accent: {
                default: '#0078D4',
                subtle: '#106EBE'
              },
              attention: {
                default: '#D13438',
                subtle: '#A80000'
              },
              good: {
                default: '#107C10',
                subtle: '#0B5A08'
              },
              warning: {
                default: '#F7B731',
                subtle: '#CA6D00'
              }
            }
          }
        },
        actions: {
          actionsOrientation: 'horizontal',
          actionAlignment: 'stretch',  // Full width buttons for M365 compliance
          buttonSpacing: 8,
          maxActions: 5,
          showCard: {
            actionMode: 'inline',
            inlineTopMargin: 8
          }
        }
      });

      // Register Action.Submit handler
      adaptiveCard.onExecuteAction = (action: AdaptiveCards.Action) => {
        if (action instanceof AdaptiveCards.SubmitAction) {
          if (onAction) {
            const submitData = action.data || {};
            onAction(submitData);
          }
        } else if (action instanceof AdaptiveCards.OpenUrlAction) {
          // Open URL actions handled by SDK automatically
          const urlAction = action as AdaptiveCards.OpenUrlAction;
          if (urlAction.url) {
            window.open(urlAction.url, '_blank', 'noopener,noreferrer');
          }
        }
      };

      // Parse and render the card
      adaptiveCard.parse(cardJson);
      const renderedCard = adaptiveCard.render();

      if (renderedCard) {
        containerRef.current.appendChild(renderedCard);
      } else {
        throw new Error('Failed to render Adaptive Card');
      }

    } catch (error: any) {
      console.error('❌ Adaptive Card rendering error:', error);
      setRenderError(error.message || 'Failed to render Adaptive Card');
    }

    // Cleanup function to prevent memory leaks
    return () => {
      // Clear event handlers
      if (adaptiveCard) {
        adaptiveCard.onExecuteAction = undefined;
      }
      
      // Clear markdown processor
      AdaptiveCards.AdaptiveCard.onProcessMarkdown = undefined;
      
      // Clear DOM content
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [cardJson, onAction]);

  // Render error state
  if (renderError) {
    return (
      <div className={styles.errorContainer} role="alert">
        <strong>Card Rendering Error</strong>
        <br />
        {renderError}
        {fallbackText && (
          <>
            <br /><br />
            <strong>Fallback:</strong> {fallbackText}
          </>
        )}
      </div>
    );
  }

  // Render card container
  return (
    <div 
      className={styles.adaptiveCardContainer}
      ref={containerRef}
      role="region"
      aria-label="Adaptive Card content"
    />
  );
};

export default AdaptiveCardMessage;

// Generated by Copilot
// updated on: 2026-01-01 | 200+ lines added | New Component | Created Adaptive Card message renderer with full SDK integration, error handling, and action support | Component Implementation